export const calculateTrafficForecast = (targetingData = {}, segments = []) => {
  const targetingType = targetingData.targetingType ?? "segment";
  const selectedSegments = targetingData.selectedSegments ?? [];
  const selfTargeting = targetingData.selfTargeting ?? {};
  const { startDate, endDate } = targetingData;

  let availableTraffic = 0;
  let avgCPM = 0;
  let audienceDescription = "";

  const start = startDate ? new Date(startDate) : new Date();
  const end = endDate
    ? new Date(endDate)
    : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
  const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  const months = (days / 30).toFixed(1);

  if (targetingType === "segment") {
    const selectedSegmentNames = [];
    selectedSegments.forEach((segId) => {
      const segment = segments.find((s) => s.uuid === segId);
      if (segment) {
        selectedSegmentNames.push(segment.name);
        availableTraffic += Math.floor(Math.random() * 1500000) + 500000;
      }
    });
    avgCPM = (Math.random() * 4 + 4).toFixed(2);
    audienceDescription = selectedSegmentNames.join(", ");
  } else {
    const {
      country = "United States",
      regions = [],
      daysOfWeek = ["mon", "tue", "wed", "thu", "fri"],
      appWhitelist = [],
      appBlacklist = [],
    } = selfTargeting;
    const countryTraffic = {
      "United States": 5000000,
      Canada: 1500000,
      "United Kingdom": 2000000,
      Germany: 1800000,
      France: 1600000,
      Australia: 1200000,
      Japan: 2500000,
      Brazil: 2200000,
      Mexico: 1800000,
      Spain: 1400000,
    };

    availableTraffic = countryTraffic[country] || 1000000;
    if (regions && regions.length > 0)
      availableTraffic = Math.floor(availableTraffic * 0.3 * regions.length);
    availableTraffic = Math.floor(availableTraffic * (daysOfWeek.length / 7));
    if (appWhitelist && appWhitelist.length > 0)
      availableTraffic = Math.floor(
        availableTraffic * 0.2 * appWhitelist.length,
      );
    if (appBlacklist && appBlacklist.length > 0)
      availableTraffic = Math.floor(availableTraffic * 0.95);

    const countryCPM = {
      "United States": 7.5,
      Canada: 6.2,
      "United Kingdom": 6.8,
      Germany: 5.9,
      France: 5.5,
      Australia: 6.5,
      Japan: 8.2,
    };
    avgCPM = (countryCPM[country] || 5.0).toFixed(2);
    audienceDescription =
      country +
      (regions && regions.length > 0 ? " - " + regions.join(", ") : "");
  }

  const periodTraffic = Math.floor(availableTraffic * (days / 30));

  return {
    availableTraffic: periodTraffic,
    avgCPM: parseFloat(avgCPM),
    audienceDescription,
    campaignDays: days,
    campaignMonths: months,
  };
};

export const calculateBudget = (cpm, impressions, startDate, endDate) => {
  const totalCost = (cpm * impressions) / 1000;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  const dailyBudget = totalCost / days;
  const estReach = Math.floor(impressions / 3);

  return {
    totalCost: totalCost.toFixed(2),
    dailyBudget: dailyBudget.toFixed(2),
    estReach: estReach.toLocaleString("en-US"),
  };
};

// Single source of truth for a campaign's "budget used" on the client.
// It is the real per-serve spend delivered by the per-campaign statistics
// endpoint — NOT impressions × cpm, which is 0 for transit campaigns that have
// no fixed cpm. The endpoint shapes it per role: an admin gets our buy-side
// cost (the income board's basis), the campaign's owner gets what they were
// charged (the sell side), so neither side ever sees the other's amount.
// budgetCap is enforced on the demand side (`charged`), so the budget bar
// reads that for every role; `spent` is the fallback for a stats object that
// predates the field (same figure for an owner).
export const campaignBudgetUsed = (stats) =>
  Number(stats?.charged ?? stats?.spent ?? 0);

export const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    amount,
  );
export const formatNumber = (num) => new Intl.NumberFormat("en-US").format(num);
export const calculateCTR = (clicks, impressions) =>
  !impressions ? "0.00" : ((clicks / impressions) * 100).toFixed(2);
export const calculateConversionRate = (conversions, clicks) =>
  !clicks ? "0.00" : ((conversions / clicks) * 100).toFixed(2);
